import axios from "axios";
import { useAuthStore } from "./auth";
import { queryClient } from "./queryClient";

const api = axios.create({
  baseURL: "/api",
  headers: { "Content-Type": "application/json" },
});

// Request interceptor: attach JWT
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * The Steward opener is DERIVED from the whole trip — every leg's date, every
 * chosen flight's departure time, every itinerary item. That makes it the one
 * cache no individual writer can be trusted to remember.
 *
 * ⚠️ THE BUG THIS CLOSES, second half. Wade moved his outbound focus flight and
 * the banner kept its old words. The server-side cache key was too narrow (see
 * turn.ts::openerSignatureOf) — but so was the client: nothing in the app ever
 * invalidated `["stewardOpener", …]`, and the query holds for five minutes, so
 * even a corrected server would have kept answering from React Query's copy.
 * A dozen writers move flights, dates and items; guarding the one Wade happened
 * to use would ship a bug that only reproduces on the path the NEXT user takes.
 *
 * So it hangs off the chokepoint every write already passes through. Cheap by
 * construction: `invalidateQueries` refetches only ACTIVE queries, so this is a
 * no-op anywhere but the trip page, and the refetch there re-runs a model call
 * only when the widened signature says the trip genuinely moved.
 */
api.interceptors.response.use((response) => {
  const method = (response.config.method ?? "get").toLowerCase();
  if (method !== "get" && method !== "head") {
    queryClient.invalidateQueries({ queryKey: ["stewardOpener"] });
  }
  return response;
});

// Response interceptor: handle 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

// Live state of a manual price-check job (GET /trips/:id/check-status, and § G23's
// group shop).
//
// ⚠️ The failure status is "error". This said "failed", which the server never
// emits (`CheckJobStatus` in services/checkJobs), so `useCheckPrices` compared
// against a value that could not occur and a HARD FAILURE fell through to the
// no-results branch — "No fares found yet — we'll keep tracking" for a check that
// had actually blown up. Found while wiring the group shop, 2026-08-17.
export interface CheckStatus {
  status: "running" | "completed" | "no_results" | "error";
  checkedRoutes: number;
  flightsFound: number;
  message?: string;
}

// Trip API
export const tripsApi = {
  getAll: () => api.get("/trips").then((r) => r.data),
  getById: (id: string) => api.get(`/trips/${id}`).then((r) => r.data),
  // create returns the trip plus an optional checkJobId — the server kicks off
  // an immediate fare check on creation so results appear ASAP; the UI polls
  // that job (see useCheckPrices initialJobId) instead of waiting for the cron.
  create: (data: { name: string; description?: string; routes: unknown[] }) =>
    api
      .post("/trips", data)
      .then((r) => r.data as { trip: { id: string }; checkJobId?: string | null }),
  update: (id: string, data: { name?: string; description?: string; active?: boolean; check_interval_hours?: number }) =>
    api.patch(`/trips/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/trips/${id}`).then((r) => r.data),
  // B2: POST /check now kicks off an async job and returns { status:'running', jobId }.
  check: (id: string) =>
    api
      .post(`/trips/${id}/check`)
      .then((r) => r.data as { status: string; jobId: string; message?: string }),
  // B2: poll a manual price-check job for live progress + final outcome.
  checkStatus: (id: string, jobId: string) =>
    api
      .get(`/trips/${id}/check-status`, { params: { jobId } })
      .then((r) => r.data as CheckStatus),
  // V2: fuller price-history points for one route on a trip.
  getRouteHistory: (tripId: string, routeId: string) =>
    api
      .get(`/trips/${tripId}/routes/${routeId}/history`)
      .then((r) => r.data as { points: import("./types").RouteHistoryPoint[] }),
  // V2: (re)generate the RT-vs-one-way + open-jaw advisor for a trip.
  generateAdvice: (tripId: string) =>
    api
      .post(`/trips/${tripId}/advice`)
      .then((r) => r.data as { advice: import("./types").TripAdvice }),
  // Email-as-UI: fetch the rendered check-in alert email HTML for in-app preview.
  emailPreview: (id: string) =>
    api
      .get(`/trips/${id}/email-preview`, { params: { type: "checkin" }, responseType: "text" })
      .then((r) => r.data as string),
  // Multi-traveler sharing: who's on the trip + their share state.
  listTravelers: (id: string) =>
    api.get(`/trips/${id}/travelers`).then((r) => r.data as { travelers: TripTraveler[] }),
  // Add a traveler to the trip; with an email, shares/invites them.
  addTraveler: (
    id: string,
    body: { travelerId?: string; name?: string; email?: string; relationship?: string; role?: "viewer" | "editor" },
  ) => api.post(`/trips/${id}/travelers`, body).then((r) => r.data),
  removeTraveler: (id: string, shareId: string) =>
    api.delete(`/trips/${id}/travelers/${shareId}`).then((r) => r.data),
};

// A traveler on a trip + their share state (GET /trips/:id/travelers).
export interface TripTraveler {
  share_id: string;
  email: string;
  role: "viewer" | "editor";
  status: "pending" | "active";
  traveler_id: string | null;
  display_name: string | null;
  relationship: string | null;
  is_user: boolean;
}

// Flights API
export const flightsApi = {
  getByRoute: (routeId: string, params?: { sort?: string; order?: string }) =>
    api.get(`/routes/${routeId}/flights`, { params }).then((r) => r.data),
};

// Alerts API
export const alertsApi = {
  getByTrip: (tripId: string) => api.get(`/alerts/trip/${tripId}`).then((r) => r.data),
  create: (tripId: string, data: { type: string; threshold?: number; channels: string[] }) =>
    api.post(`/alerts/trip/${tripId}`, data).then((r) => r.data),
  update: (id: string, data: { threshold?: number; channels?: string[]; active?: boolean }) =>
    api.put(`/alerts/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/alerts/${id}`).then((r) => r.data),
};

// Search Logs API
export const searchLogsApi = {
  getByTrip: (tripId: string) => api.get("/search-logs/trip/" + tripId).then((r) => r.data),
};

// Bookings API
export const bookingsApi = {
  getAll: () => api.get("/bookings").then((r) => r.data),
  create: (data: {
    trip_id: string;
    flight_id: string;
    confirmation_code?: string;
    notes?: string;
    price_at_booking?: number;
  }) => api.post("/bookings", data).then((r) => r.data),
  delete: (id: string) => api.delete(`/bookings/${id}`).then((r) => r.data),
};

// AI Chat API
export const aiApi = {
  recommend: (routeId: string, options?: { preferredAirline?: string | null }) =>
    api.post(`/ai/routes/${routeId}/recommend`, {
      preferredAirline: options?.preferredAirline ?? undefined,
    }).then((r) => r.data),
  refine: (routeId: string, data: { message: string; currentRecommendation?: string; tripDescription?: string }) =>
    api.post(`/ai/routes/${routeId}/refine`, data).then((r) => r.data),
  // Async parse: POST returns 202 { jobId }; poll getParseJob until it resolves.
  parseTrip: (description: string): Promise<{ jobId: string }> =>
    api.post("/ai/parse-trip", { description }).then((r) => r.data),
  getParseJob: (jobId: string) =>
    api.get(`/ai/parse-trip/${jobId}`).then((r) => r.data),
  chat: async function* (message: string, conversationHistory?: Array<{ role: string; content: string }>) {
    const token = useAuthStore.getState().token;
    const res = await fetch("/api/ai/chat", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ message, conversationHistory }),
    });
    if (res.status !== 200) {
      const err = await res.json().catch(() => ({ error: "Chat failed" }));
      throw new Error(err.error || "Chat request failed");
    }
    const reader = res.body?.getReader();
    if (reader === null || reader === undefined) return;
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        if (line.startsWith("data: ")) {
          const data = line.slice(6);
          if (data === "[DONE]") return;
          try {
            const parsed = JSON.parse(data);
            if (parsed.content) yield parsed.content;
          } catch {
            // skip malformed
          }
        }
      }
    }
  },
};


// Focus Set API
export const focusSetApi = {
  getByRoute: (routeId: string) =>
    api.get("/routes/" + routeId + "/focus-set").then((r) => r.data),
  setPreferred: (routeId: string, flightId: string) =>
    api.put("/routes/" + routeId + "/focus-set/preferred", { flightId }).then((r) => r.data),
  clearPreferred: (routeId: string) =>
    api.delete("/routes/" + routeId + "/focus-set/preferred").then((r) => r.data),
};

// Weather API — small forecast summary for a trip's destination(s).
// Backend agent is adding GET /trips/:id/weather. Shape is intentionally loose so
// callers (WeatherChip) can destructure whatever the backend returns.
export interface WeatherSummary {
  city?: string;
  code?: string;
  highF?: number | null;
  lowF?: number | null;
  precipChance?: number | null;
  summary?: string;
}
export const weatherApi = {
  get: (tripId: string) =>
    api.get(`/trips/${tripId}/weather`).then((r) => r.data as WeatherSummary | { weather: WeatherSummary[] }),
};

// Things-to-do API — suggested activities for a city + the trip's dates.
// Backend agent is adding GET /trips/:id/things-to-do?city=.
export interface ThingToDo {
  title: string;
  blurb: string;
  category: string;
}
export const thingsToDoApi = {
  get: (tripId: string, city: string) =>
    api
      .get(`/trips/${tripId}/things-to-do`, { params: { city } })
      .then((r) => r.data as { items: ThingToDo[] } | ThingToDo[]),
};

// Trip Intel briefing — async grounded briefing about the event + destination.
export interface BriefingContent {
  eventFacts: { text: string; sourceUrl?: string }[];
  destination: { text: string }[];
  logistics: { text: string }[];
  whyItMatters: string;
  sources: { title: string; url: string }[];
}
export interface BriefingResponse {
  status: "none" | "pending" | "ready" | "failed";
  briefing?: BriefingContent | null;
  seen?: boolean;
  generatedAt?: string | null;
}
export const briefingApi = {
  get: (tripId: string) =>
    api.get(`/trips/${tripId}/briefing`).then((r) => r.data as BriefingResponse),
  markSeen: (tripId: string) =>
    api.patch(`/trips/${tripId}/briefing/seen`).then((r) => r.data),
};

export { api };

// Travelers API
export const travelersApi = {
  list: () => api.get("/travelers").then((r) => r.data),
  get: (id: string) => api.get(`/travelers/${id}`).then((r) => r.data),
  me: () => api.get("/travelers/me").then((r) => r.data),
  create: (body: { display_name: string; birthdate?: string; relationship?: string }) =>
    api.post("/travelers", body).then((r) => r.data),
  update: (id: string, body: { display_name?: string; birthdate?: string; relationship?: string }) =>
    api.patch(`/travelers/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.delete(`/travelers/${id}`).then((r) => r.data),
  setPreference: (
    id: string,
    body: { kind: string; value: unknown; scope?: string; source?: string; confidence?: number },
  ) => api.post(`/travelers/${id}/preferences`, body).then((r) => r.data),
  addEmail: (id: string, body: { email: string; is_primary?: boolean }) =>
    api.post(`/travelers/${id}/emails`, body).then((r) => r.data),
  listEmails: (id: string) => api.get(`/travelers/${id}/emails`).then((r) => r.data),
  removeEmail: (id: string, emailId: string) =>
    api.delete(`/travelers/${id}/emails/${emailId}`).then((r) => r.data),
  proposals: (id: string) => api.get("/travelers/" + id + "/proposals").then((r) => r.data),
  confirmProposal: (id: string, body: { kind: string; value: unknown }) =>
    api.post("/travelers/" + id + "/proposals/confirm", body).then((r) => r.data),
  dismissProposal: (id: string, body: { kind: string; value: unknown }) =>
    api.post("/travelers/" + id + "/proposals/dismiss", body).then((r) => r.data),
};

// Companions API
export const companionsApi = {
  list: (tripId: string) => api.get(`/trips/${tripId}/companions`).then((r) => r.data),
  resolve: (tripId: string) => api.post(`/trips/${tripId}/companions/resolve`).then((r) => r.data),
  link: (
    tripId: string,
    companionId: string,
    body: { traveler_id?: string; create_name?: string; relationship?: string | null },
  ) => api.post(`/trips/${tripId}/companions/${companionId}/link`, body).then((r) => r.data),
};


// Itinerary API — day-ribbon view + add/edit/delete itinerary items.
export const itineraryApi = {
  get: (tripId: string) =>
    api
      .get(`/trips/${tripId}/itinerary`)
      .then((r) => r.data as import("./types").ItineraryResponse),
  createItem: (tripId: string, body: import("./types").ItineraryItemInput) =>
    api
      .post(`/trips/${tripId}/itinerary-items`, body)
      .then((r) => r.data as import("./types").ItemSegment),
  updateItem: (
    tripId: string,
    itemId: string,
    body: Partial<import("./types").ItineraryItemInput>,
  ) =>
    api
      .patch(`/trips/${tripId}/itinerary-items/${itemId}`, body)
      .then((r) => r.data as import("./types").ItemSegment),
  deleteItem: (tripId: string, itemId: string) =>
    api
      .delete(`/trips/${tripId}/itinerary-items/${itemId}`)
      .then((r) => r.data as { ok: boolean }),
  /**
   * Read the sailing's published day-by-day itinerary onto the trip. With no
   * `url`, the backend uses the cruise link stored in the trip description.
   *
   * A refusal comes back as a 200 with `ok: false` and a reason — "we could not
   * confirm this sailing" is an answer, not an error.
   */
  importCruise: (tripId: string, url?: string) =>
    api
      .post(`/trips/${tripId}/cruise-import`, url ? { url } : {})
      .then((r) => r.data as import("./types").CruiseImportOutcome),
};

// Trip Price API — whole-trip total + comparison ladder + per-leg source dots.
// Read-only in Phase 1 (Google-first; round-trip + airline validation layer in
// later phases). See trip-price components + spec for the framing.
export const tripPriceApi = {
  get: (tripId: string) =>
    api
      .get(`/trips/${tripId}/trip-price`)
      .then((r) => r.data as import("./types").TripPriceResponse),
};

// Unified Itinerary API — the single compacted trip surface (header + total,
// per-leg focus flight + foldable fare matrix + items, advisor + source legend).
// See the unified/ components + GET /trips/:id/plan contract.
export const tripPlanApi = {
  get: (tripId: string) =>
    api
      .get(`/trips/${tripId}/plan`)
      .then((r) => r.data as import("./types").TripPlanResponse),
};

// Flex Date Search API
export const flexApi = {
  startSearch: async (params: { tripId: string; origin: string; destination: string; startDate: string; endDate: string; tripDurationDays?: number }) => {
    const token = useAuthStore.getState().token;
    const res = await fetch("/api/flex-search", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(params),
    });
    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || "Failed to start flex search"); }
    return res.json();
  },
  getResults: async (searchId: string) => {
    const token = useAuthStore.getState().token;
    const res = await fetch(`/api/flex-search/${searchId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Failed to get flex search results");
    return res.json();
  },
  getForTrip: async (tripId: string) => {
    const token = useAuthStore.getState().token;
    const res = await fetch(`/api/flex-search/trip/${tripId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error("Failed to get flex searches");
    return res.json();
  },
};


// Nearby Flex Date Search API (extends flexApi)
export const flexNearbyApi = {
  startNearbySearch: async (params: { tripId: string; origin: string; destination: string; centerDate: string; plusMinus: number }) => {
    const token = useAuthStore.getState().token;
    const res = await fetch("/api/flex-search/nearby", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify(params),
    });
    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || "Failed to start nearby search"); }
    return res.json();
  },
};

// Travel Groups API
export interface TravelGroup {
  id: string;
  name: string;
  event_url: string | null;
  destination_airport: string | null;
  /** @deprecated § G3 — was silently the creator's first flight date. Use anchor_start_at. */
  event_start_date: string | null;
  anchor_start_at?: string | null;
  anchor_end_at?: string | null;
  anchor_timezone?: string | null;
  anchor_label?: string | null;
  organizer_name?: string | null;
  organizer_sees_fares?: boolean;
  invite_code: string;
  members: TravelGroupMember[];
  my_display_name?: string;
}
export interface TravelGroupMember {
  group_id: string;
  trip_id: string;
  mcp_user_id: string;
  display_name: string;
  joined_at: string;
  trip_name: string | null;
}
export interface FlightWindow {
  mcp_user_id: string;
  display_name: string;
  leg_role: 'outbound' | 'return';
  destination: string;
  departure_date: string;
  anchor_time: string | null;
  label: string | null;
}

/** An event in the organizer's list (§ G11). */
export interface TravelGroupSummary {
  id: string;
  name: string;
  destination_airport: string | null;
  invite_code: string;
  anchor_start_at: string | null;
  anchor_timezone: string | null;
  organizer_name: string | null;
  attendees: number;
}

export interface CreateEventBody {
  name: string;
  destination_airport: string;
  /**
   * § G24 — the meeting time as a WALL CLOCK ("2026-08-26T11:00"), meant in the
   * destination city's zone and converted there by the server. Preferred over the
   * ISO fields, which a browser can only produce using its own zone.
   */
  anchor_start_local?: string | null;
  anchor_end_local?: string | null;
  anchor_start_at?: string | null;
  anchor_end_at?: string | null;
  anchor_timezone?: string | null;
  anchor_label?: string | null;
  event_url?: string | null;
  organizer_name?: string | null;
}

export const travelGroupApi = {
  /**
   * Two shapes (§ G11). With a tripId the group is built from your own trip and
   * auto-joins you; without one it is a standalone meeting and you are the
   * organizer, not an attendee.
   */
  create: (body: CreateEventBody | { tripId: string; name: string; display_name: string; event_url?: string }) =>
    api.post('/travel-groups', body).then((r) => r.data as TravelGroup),

  listEvents: () =>
    api.get('/travel-groups/events').then((r) => r.data as TravelGroupSummary[]),

  join: (body: { tripId: string; invite_code: string; display_name: string }) =>
    api.post('/travel-groups/join', body).then((r) => r.data as TravelGroup),

  get: (groupId: string) =>
    api.get(`/travel-groups/${groupId}`).then((r) => r.data as TravelGroup),

  windows: (groupId: string) =>
    api.get(`/travel-groups/${groupId}/windows`).then((r) => r.data as FlightWindow[]),

  forTrip: (tripId: string) =>
    api.get(`/trips/${tripId}/travel-group`).then((r) => r.data as TravelGroup & { members: TravelGroupMember[] }),

  // ── Organizer (§ G4/G7/G9) ────────────────────────────────────────────────
  roster: (groupId: string) =>
    api.get(`/travel-groups/${groupId}/roster`).then((r) => r.data as Roster),

  setAnchor: (groupId: string, body: Partial<GroupAnchorInput>) =>
    api
      .patch(`/travel-groups/${groupId}/anchor`, body)
      .then((r) => r.data as TravelGroup & { retimed: AnchorRetime }),

  addAttendee: (groupId: string, body: AddAttendeeInput) =>
    api
      .post(`/travel-groups/${groupId}/attendees`, body)
      // § G23 — `checkJobId` is the attendee's first fare check, already running.
      .then(
        (r) =>
          r.data as {
            membership_id: string;
            trip_id: string;
            claim_token: string;
            checkJobId: string | null;
          }
      ),

  /** § G22 — take somebody off the roster. */
  removeAttendee: (groupId: string, membershipId: string) =>
    api
      .delete(`/travel-groups/${groupId}/attendees/${membershipId}`)
      .then((r) => r.data as { display_name: string; trip_deleted: boolean; note: string }),

  /**
   * § G23 — shop every attendee's fares now. Returns a jobId to poll; the sweep
   * runs for minutes, so there is no synchronous answer to wait for.
   */
  shop: (groupId: string) =>
    api
      .post(`/travel-groups/${groupId}/shop`)
      .then((r) => r.data as { status: 'running'; jobId: string; attendees: number }),

  shopStatus: (groupId: string, jobId: string) =>
    api
      .get(`/travel-groups/${groupId}/shop-status`, { params: { jobId } })
      .then((r) => r.data as CheckStatus),

  /** § G7 — a managed attendee takes over the trip held for them. */
  claim: (claimToken: string, displayName?: string) =>
    api
      .post('/travel-groups/claim', { claim_token: claimToken, display_name: displayName })
      .then((r) => r.data as { group_id: string; trip_id: string }),

  manifest: (groupId: string) =>
    api.get(`/travel-groups/${groupId}/manifest`).then((r) => r.data as GroupManifest),

  /**
   * Hotels and activities on ONE traveller's trip, addressed through the EVENT.
   *
   * ⚠️ Deliberately NOT `itineraryApi` with the person's `trip_id`. That path is
   * scoped to `trips.mcp_user_id = caller`, and a managed attendee's trip changes
   * owner the moment they claim it (§ G7) — so it would work for the organizer
   * right up until the attendee signed up, then start 404ing. The group-scoped
   * route authorises from the membership instead.
   */
  addManifestItem: (groupId: string, memberId: string, body: import('./types').ItineraryItemInput) =>
    api
      .post(`/travel-groups/${groupId}/members/${memberId}/itinerary-items`, body)
      .then((r) => r.data as ManifestItem),

  updateManifestItem: (
    groupId: string,
    memberId: string,
    itemId: string,
    body: Partial<import('./types').ItineraryItemInput>
  ) =>
    api
      .patch(`/travel-groups/${groupId}/members/${memberId}/itinerary-items/${itemId}`, body)
      .then((r) => r.data as ManifestItem),

  removeManifestItem: (groupId: string, memberId: string, itemId: string) =>
    api
      .delete(`/travel-groups/${groupId}/members/${memberId}/itinerary-items/${itemId}`)
      .then((r) => r.data as { ok: boolean }),

  listOrganizers: (groupId: string) =>
    api.get(`/travel-groups/${groupId}/organizers`).then((r) => r.data as EventOrganizer[]),

  inviteOrganizer: (groupId: string, email: string) =>
    api
      .post(`/travel-groups/${groupId}/organizers`, { email })
      .then((r) => r.data as InviteOrganizerResult),

  removeOrganizer: (groupId: string, organizerId: string) =>
    api.delete(`/travel-groups/${groupId}/organizers/${organizerId}`).then((r) => r.data as { ok: boolean }),

  resolveAttendee: (groupId: string, membershipId: string, decision: 'confirm' | 'remove') =>
    api
      .patch(`/travel-groups/${groupId}/attendees/${membershipId}`, { decision })
      .then((r) => r.data as { ok: boolean }),

  /**
   * § G20 — send this attendee their flight options.
   *
   * `sent: false` with a 200 is a real outcome, not an error: the options were
   * composed but SMTP is not configured, so the organizer is told the mail did
   * not go rather than being left to assume it did.
   */
  /**
   * § G27 — change an attendee's departure airport and/or travel dates.
   *
   * Open to the organizer AND to the traveller themselves. A successful edit
   * DISCARDS the fares that described the old plan (they are a different
   * journey, not a stale price) and immediately reshops, so `fares_cleared` is
   * a fact worth showing rather than an implementation detail.
   */
  editAttendeeItinerary: (
    groupId: string,
    membershipId: string,
    body: {
      home_airport?: string;
      alternate_airports?: string[];
      outbound_date?: string;
      return_date?: string;
      travel_mode?: TravelMode;
      travel_mode_note?: string | null;
    }
  ) =>
    api
      .patch(`/travel-groups/${groupId}/attendees/${membershipId}/itinerary`, body)
      .then(
        (r) =>
          r.data as {
            membership_id: string;
            trip_id: string | null;
            is_local: boolean;
            travel_mode: TravelMode;
            routes_rebuilt: number;
            fares_cleared: number;
            message: string;
            checkJobId: string | null;
          }
      ),

  /**
   * § G28 — the data behind the printable options sheet. Returns the SAME
   * TripView the email renders, so print and email can never quote different
   * fares. `trip_view` is left loosely typed on purpose: this page renders it,
   * it does not reason about it, and mirroring the whole backend TripView here
   * would be a second definition to drift.
   */
  flightOptionsForPrint: (groupId: string, membershipId: string) =>
    api
      .get(`/travel-groups/${groupId}/attendees/${membershipId}/flight-options/print`)
      .then((r) => r.data as FlightOptionsPrintData),

  sendFlightOptions: (groupId: string, membershipId: string) =>
    api
      .post(`/travel-groups/${groupId}/attendees/${membershipId}/flight-options`)
      .then((r) => r.data as { sent: boolean; to: string; options: number }),
};

/** § G28 — what the printable options sheet renders. */
export interface FlightOptionsPrintData {
  attendee_name: string;
  event_name: string;
  when_line: string | null;
  options: number;
  trip_view: { legs?: any[] } & Record<string, any>;
}

/** § G16 — every booked flight in the group, on one page. */
export interface ManifestLeg {
  leg_role: 'outbound' | 'return';
  origin: string;
  destination: string;
  departure_date: string | null;
  booked: boolean;
  /** "DL1234", or null when the booking recorded no identity. Never invented. */
  flight: string | null;
  when: string | null;
  booking_departure_date: string | null;
  booking_ref: string | null;
  /** Null for non-organizers — § G6 keeps money organizer-only. */
  fare: number | null;
  price_basis: 'per_person' | 'total' | null;
  /**
   * What shopping is steering this leg toward. Shown as its OWN fact, clearly
   * labelled — a focus flight is not a ticket, and a booked leg's identity comes
   * from the booking record or stays blank.
   */
  focus_flight: string | null;
  /**
   * `false` is the interesting state: the leg is ticketed on one flight while
   * `when` was computed from another, so the time must be qualified rather than
   * presented as this traveller's arrival. `null` = the question does not arise.
   */
  focus_matches_booking: boolean | null;
}

/** An `itinerary_items` row (026) — a hotel, a dinner, an activity. */
export interface ManifestItem {
  id: string;
  item_type: 'event' | 'lodging' | 'dining' | 'activity' | 'transfer' | 'note';
  title: string;
  city: string | null;
  location: string | null;
  day_date: string | null;
  start_time: string | null;
  end_time: string | null;
  notes: string | null;
  /** Its presence is what marks an add-on booked. */
  confirmation: string | null;
  /** Null for non-organizers — § G6. */
  cost: number | null;
}

export interface ManifestPerson {
  display_name: string;
  home_airport: string | null;
  passengers: number;
  legs: ManifestLeg[];
  complete: boolean;
  /** The membership row — how a write is addressed to this person. */
  member_id: string | null;
  /** Where their itinerary items live. Null when no trip exists yet. */
  trip_id: string | null;
  items: ManifestItem[];
  /** § G33 — 'flight' | 'car' | 'train' | 'bus' | 'other'. */
  travel_mode: string;
  travel_mode_note: string | null;
}

export interface GroupManifest {
  event: {
    id: string;
    name: string;
    destination_airport: string | null;
    anchor_start_at: string | null;
    anchor_end_at: string | null;
    anchor_timezone: string | null;
    anchor_label: string | null;
    event_url: string | null;
    /** § G21 — where the meeting is, in the organizer's own words. */
    venue: string | null;
  };
  people: ManifestPerson[];
  outstanding: { display_name: string; unbooked: number }[];
  travellers: number;
  booked_legs: number;
  total_legs: number;
  amount: number | null;
  amount_label: string;
  summary: string;
}

/** Someone who can run an event (§ G12). */
/**
 * What inviting a co-organizer reports back (§ G37).
 *
 * `email_sent` is the AWAITED outcome of the send, not an assumption — the server
 * waits for SMTP before answering, so the surface can say "email sent" and be
 * right. `email_log_id` is the stored message: the link target for showing the
 * organizer the email exactly as the recipient received it. Null means the send
 * happened but could not be logged, which costs the preview and nothing else.
 */
export interface InviteOrganizerResult {
  email: string;
  status: 'pending' | 'active' | 'revoked';
  email_sent: boolean;
  email_log_id: string | null;
}

/** A stored outbound email, as its recipient saw it (§ G37). */
export interface StoredEmailPreview {
  id: string;
  to_email: string;
  subject: string;
  /** The delivered bytes, scrubbed of anything executable server-side. */
  html: string;
  sent_at: string | null;
  status: string;
  error: string | null;
  email_type: string | null;
}

export const emailApi = {
  /**
   * The email as it was SENT (`email_log.html_body`), not a re-render — a
   * re-render drifts with the template and shows a message nobody received.
   */
  preview: (emailLogId: string) =>
    api.get(`/emails/${emailLogId}/preview`).then((r) => r.data as StoredEmailPreview),
};

export interface EventOrganizer {
  id: string;
  mcp_user_id: string | null;
  email: string;
  status: 'pending' | 'active' | 'revoked';
  is_owner: boolean;
  invited_by: string;
  created_at: string;
}

export interface GroupAnchorInput {
  anchor_start_at: string | null;
  anchor_end_at: string | null;
  anchor_timezone: string | null;
  anchor_label: string | null;
  destination_airport: string | null;
  organizer_sees_fares: boolean;
  /** § G21 — where the meeting is, in the organizer's own words. */
  venue: string | null;
  /** § G21 — deplane + airport→venue + door buffer, in minutes. */
  venue_transit_minutes: number | null;
}

/**
 * What a PATCH /anchor did to the roster's legs (§ G21).
 *
 * ⚠️ `anchor_end_at`, `venue` and `venue_transit_minutes` are WRITE-THROUGH on
 * the server: sending the key with null CLEARS it (the other fields treat null as
 * "leave alone"). So only send the keys you mean to change — which is why the
 * form posts a whole window at once rather than field by field.
 */
export interface AnchorRetime {
  updated: number;
  left_alone: number;
  window: MeetingWindow;
}

export interface AddAttendeeInput {
  display_name: string;
  home_airport: string;
  email?: string | null;
  passengers?: number;
  outbound_date?: string | null;
  return_date?: string | null;
  /**
   * § G19 — the other airports this person might use. "Ana is in Manhattan"
   * means JFK *or* EWR *or* LGA; stored as non-primary variant routes so a
   * candidate never counts as a second trip.
   */
  alternate_airports?: string[];
}

/** How the attendee got here (§ G1). `proposed` = they forwarded a booking (§ G9). */
export type MemberStatus = 'managed' | 'invited' | 'joined' | 'proposed';

export type AnchorVerdict =
  | 'clears' | 'tight' | 'misses' | 'no_flight' | 'no_anchor' | 'unknown' | 'unknown_timezone'
  /** § G25 — already in the meeting's city. No flight, and none needed. */
  | 'local'
  /** § G33 — driving, training, busing or arranging their own way in. */
  | 'own_transport';

/** § G33 — how an attendee reaches the meeting. Anything but 'flight' = do not shop. */
export type TravelMode = 'flight' | 'car' | 'train' | 'bus' | 'other';

export interface RosterLeg {
  leg_role: 'outbound' | 'return';
  origin: string;
  destination: string;
  departure_date: string;
  anchor_time: string | null;
  /** Null when fares are not visible to the organizer, or none is recorded. */
  fare: number | null;
  price_basis: 'per_person' | 'total' | null;
  /**
   * § G23 — cheapest fare SHOPPING found for this leg, per person. An ESTIMATE:
   * never added to `fare`, never rendered as money anybody paid.
   */
  shopped_fare?: number | null;
  /** How many options that sweep stored for this leg. */
  shopped_options?: number;
  booked: boolean;
  label: string | null;
  timezone?: string | null;
}

export interface RosterMember {
  membership_id: string;
  display_name: string;
  mcp_user_id: string | null;
  status: MemberStatus;
  home_airport: string | null;
  passengers: number;
  trip_id: string | null;
  legs: RosterLeg[];
  /** § G25 — already in the meeting's metro; no flight to find or chase. */
  is_local?: boolean;
  /** § G33 — how they are getting there. */
  travel_mode?: TravelMode;
  travel_mode_note?: string | null;
  /** § G19 — other airports this person might depart from. Primary excluded. */
  candidate_origins: string[];
  anchor: { verdict: AnchorVerdict; margin_minutes: number | null; note: string | null };
}

export interface GroupTotal {
  /** Null means unknown — never render 0 in its place. */
  amount: number | null;
  counted_legs: number;
  uncounted_legs: number;
  complete: boolean;
  label: string;
}

/** § G23 — what shopping FOUND, as an estimate. Never merged with GroupTotal. */
export interface GroupShoppedTotal {
  /** Sum of cheapest shopped fares x party size. Null when nothing is priced. */
  amount: number | null;
  priced_legs: number;
  unpriced_legs: number;
  /** Options stored across the group. 0 = nothing has been shopped at all. */
  options: number;
}

/** One origin city — a tab in the organizer's UI (§ G10). */
export interface OriginGroup {
  /** Stable metro KEY — "New York", "STL", or "?" when no origin is set. */
  origin: string;
  /** What the tab reads: "New York", "St. Louis", "Origin not set" (§ G18). */
  label: string;
  /** The airports actually in play here, sorted: ["EWR", "JFK", "LGA"]. */
  airports: string[];
  members: RosterMember[];
  travellers: number;
  fully_booked: number;
  worst_verdict: AnchorVerdict;
  total: GroupTotal | null;
  shopped?: GroupShoppedTotal | null;
  /** The money line for this city. Server-composed so it cannot disagree. */
  fare_headline?: string;
}

/** § G21 — the hours the meeting leaves for flying, computed server-side. */
export interface MeetingWindow {
  /** "HH:MM" at the destination airport, or null when it cannot be claimed. */
  latest_arrival: string | null;
  earliest_departure: string | null;
  /** Why a null is null, or what the values mean. Safe to render as-is. */
  note: string | null;
}

export interface Roster {
  /** The event itself, so the roster can stand alone without a trip page. */
  event: {
    id: string;
    name: string;
    destination_airport: string | null;
    invite_code: string;
    organizer_name: string | null;
    event_url: string | null;
    /** § G21 — where it is, when it ends, how far the venue is, and the window. */
    venue: string | null;
    venue_transit_minutes: number | null;
    anchor_end_at: string | null;
    window: MeetingWindow;
    /**
     * § G24 — the destination CITY's IANA zone. The meeting's wall-clock times are
     * read and written in this zone; `anchor.timezone` is merely what got stored,
     * which for events created before G24 is the organizer's laptop zone.
     */
    destination_timezone: string | null;
  };
  anchor: { start_at: string | null; timezone: string | null; label: string | null; ground_minutes: number | null };
  fares_visible: boolean;
  members: RosterMember[];
  origins: OriginGroup[];
  total: GroupTotal | null;
  /** § G23 — what shopping found. Null when § G6 hides fares. */
  shopped?: GroupShoppedTotal | null;
  /** The single money line for the header, composed server-side. */
  fare_headline?: string;
  coverage: {
    attendees: number;
    travellers: number;
    managed: number;
    invited: number;
    joined: number;
    proposed: number;
    fully_booked: number;
    partly_booked: number;
    no_flights: number;
    /** § G25 — attendees already in the meeting's city. Nothing to chase. */
    local?: number;
    /** § G33 — attendees driving/training/busing in. Nothing to shop. */
    not_flying?: number;
    label: string;
  };
}

// ── Trip Completeness Coach ──────────────────────────────────────────────────

export type CompletenessElementKey =
  | 'depart_transport' | 'arrival_transfer' | 'lodging'
  | 'activities' | 'return_transfer' | 'home_transport';

export interface CompletenessChoice {
  value: string;
  label: string;
}

export interface CompletenessSuggestion {
  suggestedChoice?: string | null;
  note?: string | null;
  hotelName?: string | null;
  hotelDeadline?: string | null;
}

export interface CompletenessElement {
  key: CompletenessElementKey;
  kind: 'transport' | 'lodging' | 'activities';
  label: string;
  hint: string;
  status: 'open' | 'resolved' | 'dismissed';
  choice?: string | null;
  detail?: Record<string, unknown> | null;
  options: CompletenessChoice[];
  suggestion?: CompletenessSuggestion | null;
}

export interface CompletenessResult {
  elements: CompletenessElement[];
  doneCount: number;
  totalCount: number;
}

export const completenessApi = {
  get: (tripId: string) =>
    api.get(`/trips/${tripId}/completeness`).then((r) => r.data as CompletenessResult),

  set: (tripId: string, key: CompletenessElementKey, body: { status: 'resolved' | 'dismissed'; choice?: string | null; detail?: Record<string, unknown> | null }) =>
    api.put(`/trips/${tripId}/completeness/${key}`, body).then((r) => r.data as CompletenessResult),

  clear: (tripId: string, key: CompletenessElementKey) =>
    api.delete(`/trips/${tripId}/completeness/${key}`).then((r) => r.data as CompletenessResult),

  refresh: (tripId: string) =>
    api.post(`/trips/${tripId}/completeness/refresh`).then((r) => r.data as CompletenessResult),
};

// Booking status API — manual mark / ingestion / confirmed-hold seam.
export const bookingApi = {
  setStatus: (
    routeId: string,
    body: {
      status: "none" | "held" | "booked";
      booking_ref?: string | null;
      hold_expires_at?: string | null;
      /** Cash actually paid (status "booked"), in the unit given by price_basis. */
      price_paid?: number | null;
      /** What price_paid means. Server default: "per_person". */
      price_basis?: "per_person" | "total";
      /** How many travellers price_paid covered. Defaults to the trip's count. */
      passengers?: number | null;
      /** WHICH flight was booked. The server writes the booking to the route that
       *  OWNS this flight (a direction is several date-variant routes) and pins
       *  the focus to it — the Sep 12 / Sep 13 fix. */
      flight_id?: string | null;
      /** Traveller-stated identity, used when the departure is one we don't track. */
      booked_airline?: string | null;
      booked_flight_number?: string | null;
      booked_departure_date?: string | null;
      currency?: string;
      /** "airline_site" | "ota" | "other" | "in_app". */
      source?: string | null;
      fare_family?: string | null;
    },
  ) =>
    api.patch(`/routes/${routeId}/booking`, body).then(
      (r) => r.data as { route: Record<string, unknown>; retargeted: boolean; tracked: boolean },
    ),

  /** The carrier (or Google Flights fallback) HANDOFF for a leg (B2). Pass the
   *  focus flight's airline so the link matches the flight shown on screen.
   *  Server-authored (T5) — it also states what the URL carries and where the
   *  traveller lands, which the UI renders verbatim rather than guessing. */
  getBookingLink: (routeId: string, airline?: string | null) =>
    api
      .get(`/routes/${routeId}/booking-link`, { params: airline ? { airline } : undefined })
      .then((r) => r.data as import("./types").BookingHandoff),

  /** A single round-trip handoff for a same-carrier round trip. Resolves to
   *  null when the two legs don't form a clean round trip (HTTP 204). */
  getRoundTripLink: (outboundRouteId: string, returnRouteId: string, airline?: string | null) =>
    api
      .get(`/routes/roundtrip-booking-link`, {
        params: { outboundRouteId, returnRouteId, ...(airline ? { airline } : {}) },
      })
      .then((r) => (r.status === 204 ? null : (r.data as import("./types").BookingHandoff))),

  /**
   * Remember the handoff so "did you book it?" survives a reload (B2 → B5).
   *
   * ⚠️ NOT a booking write. It touches only the prompt-state columns; a tap on
   * a link is not a purchase and must never be recorded as one (B6). The single
   * seam that records a booking is `setStatus` above, shared with email
   * ingestion.
   *
   * `routeIds` marks every direction one round-trip handoff covered.
   */
  markHandoff: (
    routeId: string,
    action: "opened" | "not_yet",
    routeIds?: string[],
  ) =>
    api
      .post(`/routes/${routeId}/booking-handoff`, { action, ...(routeIds ? { routeIds } : {}) })
      .then((r) => r.data as { action: string; routes: Array<{ id: string }> }),

  requestHold: (routeId: string, body: { airline_code: string; airline_name: string; username: string; password: string; save: boolean }) =>
    api.post(`/routes/${routeId}/book-hold`, body).then((r) => r.data as { status: string; saved: boolean; message: string }),
};

// Route update API
export const routeUpdateApi = {
  /** Re-gather flights for one leg now (e.g. when a scrape under-returned). */
  recheck: async (routeId: string) => {
    const token = useAuthStore.getState().token;
    const res = await fetch("/api/routes/" + routeId + "/recheck", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
    });
    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || "Failed to re-search"); }
    return res.json() as Promise<{ checkJobId: string | null }>;
  },

  updateDepartureDate: async (routeId: string, departureDate: string) => {
    const token = useAuthStore.getState().token;
    const res = await fetch("/api/routes/" + routeId, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ departure_date: departureDate }),
    });
    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || "Failed to update route"); }
    return res.json();
  },
};

/* Steward Trip Workspace API.

   `turn` is a real token stream (an upgrade over aiApi.chat's buffer-then-fake-
   chunk): the backend forwards Aspen's tokens live through the directive
   stripper, so prose arrives as it's written and cards arrive as typed action
   frames. The parser buffers across chunk boundaries and splits on newlines —
   an SSE frame is routinely cut in half by TCP.

   There is deliberately NO add-to-itinerary call here: the card's Add button
   uses itineraryApi.createItem, the existing write path. */
export const stewardApi = {
  /** The proactive opener. `opener: null` means TripDetail shows no banner. */
  opener: (tripId: string) =>
    api
      .get(`/trips/${tripId}/steward/opener`)
      .then((r) => r.data as import("./types").StewardOpener),

  /**
   * The trip they already described, as rows they can accept. `proposals: []`
   * means TripDetail shows no card — either nothing was disclosed, or all of it
   * is already on the itinerary.
   */
  backbone: (tripId: string) =>
    api
      .get(`/trips/${tripId}/steward/backbone`)
      .then((r) => r.data as import("./types").StewardBackbone),

  /**
   * The thread so far. Re-opening the workspace RESUMES the conversation — the
   * turns were always persisted server-side; this is what finally reads them
   * back.
   */
  conversation: (tripId: string) =>
    api
      .get(`/trips/${tripId}/steward/conversation`)
      .then((r) => r.data as import("./types").StewardConversation),

  /** Stream one turn. Yields typed events; no auto-retry (duplicate-turn hazard). */
  turn: async function* (
    tripId: string,
    body: {
      message: string;
      conversationId?: string | null;
      /** Add-slot context: which day, which city, what kind of thing. */
      scope?: import("./types").StewardScope | null;
    },
    signal?: AbortSignal,
  ): AsyncGenerator<import("./types").StewardTurnEvent> {
    const token = useAuthStore.getState().token;
    const res = await fetch(`/api/trips/${tripId}/steward/turn`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || "Steward is unavailable right now");
    }

    const reader = res.body?.getReader();
    if (!reader) return;
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // Keep the trailing partial line for the next read.
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const data = line.slice(6);
        if (data === "[DONE]") return;
        try {
          yield JSON.parse(data) as import("./types").StewardTurnEvent;
        } catch {
          // keep-alive or split frame — ignore
        }
      }
    }
  },

  /** Card photos are proxied (SSRF-guarded, 2MB cap), never hotlinked. */
  imageUrl: (src: string) => `/api/steward/image?url=${encodeURIComponent(src)}`,

  /** Keyless map affordances — no Places/Maps API key needed. */
  mapsLink: (query: string) =>
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`,
  mapsEmbed: (query: string) =>
    `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`,
};
